import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import {
  listHealthChecksForService,
  updateHealthCheckForService,
} from "../api/client";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";

export function PageHealthCheckEdit() {
  const navigate = useNavigate();
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { healthCheckId } = useParams<{ healthCheckId?: string }>();

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [probeType, setProbeType] = useState<"liveness" | "liveness_dotnet_health">("liveness");
  const [probeIntervalMS, setProbeIntervalMS] = useState(10000);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (!serviceSlug || !healthCheckId) {
      setInitialLoading(false);
      return;
    }
    const abort = new AbortController();
    setInitialLoading(true);
    setLoadError(null);
    listHealthChecksForService(serviceSlug, abort.signal)
      .then((list) => {
        if (abort.signal.aborted) return;
        const found = list.find((hc) => hc.id === healthCheckId);
        if (found) {
          setName(found.name);
          setUrl(found.url);
          setProbeType(
            found.probe_type === "liveness_dotnet_health"
              ? "liveness_dotnet_health"
              : "liveness",
          );
          setProbeIntervalMS(found.probe_interval_ms);
          setEnabled(found.enabled);
        } else {
          setLoadError("Health check not found.");
        }
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setLoadError(err instanceof Error ? err.message : "Failed to load health check");
      })
      .finally(() => {
        if (!abort.signal.aborted) setInitialLoading(false);
      });
    return () => abort.abort();
  }, [serviceSlug, healthCheckId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!serviceSlug || !healthCheckId) return;

    if (!name.trim() || !url.trim()) {
      setSubmitError("Name and URL are required");
      return;
    }

    if (probeIntervalMS < 1000) {
      setSubmitError("Interval must be at least 1000ms (1 second)");
      return;
    }

    try {
      setLoading(true);
      setSubmitError(null);
      await updateHealthCheckForService(
        serviceSlug,
        healthCheckId,
        {
          name: name.trim(),
          url: url.trim(),
          probe_type: probeType,
          probe_interval_ms: probeIntervalMS,
          enabled,
        },
        undefined,
      );
      navigate(`/services/${encodeURIComponent(serviceSlug)}/health-checks`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to update health check");
    } finally {
      setLoading(false);
    }
  };

  if (!serviceSlug || !healthCheckId) {
    return (
      <Alert severity="warning">
        Missing service or health check in URL. Please navigate from a service page.
      </Alert>
    );
  }

  if (initialLoading) {
    return (
      <Panel title="Edit Health Check" subtitle={`Service: ${serviceSlug}`}>
        <PageLoadingState message="Loading health check..." />
      </Panel>
    );
  }

  if (loadError) {
    return (
      <Panel title="Edit Health Check" subtitle={`Service: ${serviceSlug}`}>
        <PageErrorState message={loadError} />
        <Button
          variant="text"
          onClick={() => navigate(`/services/${encodeURIComponent(serviceSlug)}/health-checks`)}
          sx={{ mt: 1 }}
        >
          Back to service
        </Button>
      </Panel>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Panel
        title="Edit Health Check"
        subtitle={`Service: ${serviceSlug}`}
        toolbar={
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              form="health-check-edit-form"
              disabled={loading}
            >
              Save
            </Button>
          </Stack>
        }
      >
        {submitError ? (
          <PageErrorState message={submitError} sx={{ mb: 1.5 }} />
        ) : null}
        <Box
          id="health-check-edit-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxWidth: 720,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="API Health Check"
          />
          <TextField
            fullWidth
            size="small"
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            placeholder="https://api.example.com/health"
            type="url"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Probe Type</InputLabel>
            <Select
              value={probeType}
              label="Probe Type"
              onChange={(e) =>
                setProbeType(e.target.value as "liveness" | "liveness_dotnet_health")
              }
            >
              <MenuItem value="liveness">Liveness (HTTP Status Code)</MenuItem>
              <MenuItem value="liveness_dotnet_health">
                Liveness (.NET Health Check)
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Probe Interval (milliseconds)"
            value={probeIntervalMS}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val)) setProbeIntervalMS(val);
            }}
            type="number"
            inputProps={{ min: 1000, step: 1000 }}
            helperText={`${Math.round(probeIntervalMS / 1000)} seconds`}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label="Enabled"
          />
          {probeType === "liveness_dotnet_health" && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This will parse the .NET health check JSON response and track main
              status plus subsystem statuses (e.g., sqlserver, redis).
            </Alert>
          )}
        </Box>
      </Panel>
    </Box>
  );
}
